/**
 * RecipesTab — Bill of Materials editor for Petpooja menu items.
 *
 * Sprint 2026-08 · Phase 7.
 *
 * Progressive onboarding workflow:
 *   1. Petpooja sync creates MenuItem rows automatically (see PetpoojaSyncTab).
 *   2. This tab surfaces the "unmapped queue" — menu items sold but with no
 *      recipe. Owner maps top-sellers first.
 *   3. Building a recipe → BOM: pick raw material + qty + UOM per serving.
 *      Retro-apply defaults ON so historical sales get retroactively deducted.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    ChefHat,
    Plus,
    Trash2,
    Sparkles,
    AlertCircle,
    Search,
    Pencil,
    TrendingUp,
    TrendingDown,
    Beaker,
    Play,
    Factory,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { api, fmtMoney } from '../../lib/api';


// Sprint 2026-08 · Phase 7c
// -------------------------------------------------------------------
// RecipesTab is now split into TWO sub-modes:
//   • "menu"  → existing menu-item recipes + margin/food-cost columns
//   • "prep"  → prep recipes + Log-Batch flow (intermediate base batches)
// A sub-tab strip switches between them; both share the same tab
// container mount so refetches stay minimal.
export function RecipesTab() {
    const [mode, setMode] = useState(() => {
        try { return localStorage.getItem('emb.recipes.mode') || 'menu'; } catch { return 'menu'; }
    });
    const change = (v) => {
        setMode(v);
        try { localStorage.setItem('emb.recipes.mode', v); } catch { /* noop */ }
    };
    return (
        <div data-testid="recipes-tab">
            <Tabs value={mode} onValueChange={change} className="w-full">
                <TabsList className="grid grid-cols-3 max-w-md mb-4">
                    <TabsTrigger value="menu" data-testid="recipes-mode-menu">
                        <ChefHat size={13} className="mr-1.5" /> Menu
                    </TabsTrigger>
                    <TabsTrigger value="prep" data-testid="recipes-mode-prep">
                        <Beaker size={13} className="mr-1.5" /> Prep
                    </TabsTrigger>
                    <TabsTrigger value="production" data-testid="recipes-mode-production">
                        <Factory size={13} className="mr-1.5" /> Production
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="menu"><MenuRecipes /></TabsContent>
                <TabsContent value="prep"><PrepRecipes /></TabsContent>
                <TabsContent value="production"><ProductionRecipes /></TabsContent>
            </Tabs>
        </div>
    );
}


function MenuRecipes() {
    const [menuItems, setMenuItems] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [uomList, setUomList] = useState([]);
    const [margins, setMargins] = useState({});    // { menu_item_id: margin row }
    const [defaultThreshold, setDefaultThreshold] = useState(33);
    const [filter, setFilter] = useState('unmapped');   // 'all' | 'mapped' | 'unmapped'
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null);       // MenuItem being edited

    const load = useCallback(async () => {
        setMenuItems(null);
        try {
            const [m, r, mats, uom, mgn] = await Promise.all([
                api.listMenuItems(),
                api.listRecipes(),
                api.stockRawMaterials(),
                api.stockUomMaster(),
                api.recipesMargins().catch(() => ({ items: [], default_threshold_pct: 33 })),
            ]);
            setMenuItems(m.items || []);
            setRecipes(r.items || []);
            setRawMaterials(mats.items || []);
            setUomList(uom.units || []);
            const marginMap = {};
            for (const row of (mgn.items || [])) marginMap[row.menu_item_id] = row;
            setMargins(marginMap);
            setDefaultThreshold(mgn.default_threshold_pct || 33);
        } catch (e) {
            setMenuItems([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const recipeByMenuId = useMemo(() => {
        const m = {};
        for (const r of recipes) m[r.menu_item_id] = r;
        return m;
    }, [recipes]);

    const filtered = useMemo(() => {
        const list = menuItems || [];
        return list.filter((mi) => {
            if (filter === 'mapped' && !mi.recipe_id) return false;
            if (filter === 'unmapped' && mi.recipe_id) return false;
            if (search && !mi.name.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [menuItems, filter, search]);

    const unmappedCount = (menuItems || []).filter((m) => !m.recipe_id).length;

    return (
        <div className="space-y-4" data-testid="recipes-tab">
            {/* Header + progressive onboarding banner */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        <ChefHat size={18} className="text-primary" /> Recipes (Bill of Materials)
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-xl">
                        Map every menu item to its raw material recipe. Once mapped, each
                        Petpooja sale automatically deducts stock from your ledger.
                        {unmappedCount > 0 && (
                            <span className="text-amber-600 dark:text-amber-400 font-medium">
                                {' '}{unmappedCount} item{unmappedCount === 1 ? '' : 's'} still unmapped.
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search menu items…"
                            className="pl-8 w-[200px]"
                            data-testid="recipes-search"
                        />
                    </div>
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-[140px]" data-testid="recipes-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="unmapped">Unmapped only</SelectItem>
                            <SelectItem value="mapped">Mapped only</SelectItem>
                            <SelectItem value="all">All items</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Menu items list */}
            <Card className="p-0 overflow-hidden">
                {menuItems === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <ChefHat size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">
                            {(menuItems || []).length === 0 ? 'No menu items yet' : 'Nothing matches'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {(menuItems || []).length === 0
                                ? 'Upload a Petpooja Excel file to create menu items automatically.'
                                : 'Try switching filters or clearing the search.'}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="recipes-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Menu item</th>
                                    <th className="text-right px-4 py-2 font-medium">Sold</th>
                                    <th className="text-right px-4 py-2 font-medium">Sell ₹</th>
                                    <th className="text-right px-4 py-2 font-medium">Cost ₹</th>
                                    <th className="text-right px-4 py-2 font-medium">Cost %</th>
                                    <th className="text-left px-4 py-2 font-medium">Recipe</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((mi) => {
                                    const recipe = recipeByMenuId[mi.id];
                                    const margin = margins[mi.id];
                                    return (
                                        <tr key={mi.id} className="border-t border-border hover:bg-muted/30">
                                            <td className="px-4 py-2 font-medium">
                                                {mi.name}
                                                {mi.active === false && (
                                                    <Badge variant="outline" className="ml-2 text-[10px] text-muted-foreground">
                                                        86'd
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                {Number(mi.times_sold || 0).toFixed(0)}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <SellingPriceCell
                                                    menuItem={mi}
                                                    onSaved={(sp) => {
                                                        setMenuItems((prev) => prev.map((x) =>
                                                            x.id === mi.id ? { ...x, selling_price: sp } : x));
                                                        // Refresh margins in background
                                                        api.recipesMargins().then((mgn) => {
                                                            const mp = {};
                                                            for (const row of (mgn.items || [])) mp[row.menu_item_id] = row;
                                                            setMargins(mp);
                                                        }).catch(() => {});
                                                    }}
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                {margin?.food_cost != null ? fmtMoney(margin.food_cost) : '—'}
                                                {margin?.incomplete_costs?.length > 0 && (
                                                    <span
                                                        title={`Missing costs for: ${margin.incomplete_costs.join(', ')}`}
                                                        className="ml-1 text-amber-500 cursor-help"
                                                    >⚠</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <MarginCell margin={margin} defaultThreshold={defaultThreshold} />
                                            </td>
                                            <td className="px-4 py-2">
                                                {recipe ? (
                                                    <span className="text-xs text-muted-foreground">
                                                        {recipe.ingredients.length} ing
                                                    </span>
                                                ) : (
                                                    <Badge variant="outline" className="gap-1 border-amber-400/60 text-amber-700 dark:text-amber-300">
                                                        <AlertCircle size={11} /> No recipe
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <Button
                                                    size="sm"
                                                    variant={recipe ? 'ghost' : 'default'}
                                                    className="gap-1"
                                                    onClick={() => setEditing(mi)}
                                                    data-testid={`recipes-build-${mi.id}`}
                                                >
                                                    {recipe ? <><Pencil size={12} /> Edit</> : <><Sparkles size={12} /> Build</>}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {editing && (
                <RecipeBuilderDialog
                    open={!!editing}
                    onOpenChange={(v) => { if (!v) setEditing(null); }}
                    menuItem={editing}
                    existing={recipeByMenuId[editing.id]}
                    rawMaterials={rawMaterials}
                    uomList={uomList}
                    onSaved={async () => {
                        setEditing(null);
                        await load();
                    }}
                />
            )}
        </div>
    );
}


/**
 * RecipeBuilderDialog — inline dialog to add/edit a menu item's BOM.
 *
 * Retroactive apply is on by default: on save, past Petpooja sales for
 * this menu item are backfilled into the stock ledger. Owner can uncheck.
 */
function RecipeBuilderDialog({ open, onOpenChange, menuItem, existing, rawMaterials, uomList, onSaved }) {
    const [ingredients, setIngredients] = useState(() => {
        if (existing?.ingredients?.length) return existing.ingredients.map((i) => ({ ...i }));
        return [{ item_id: '', item_name: '', qty: '', uom: 'g' }];
    });
    const [notes, setNotes] = useState(existing?.notes || '');
    const [retroApply, setRetroApply] = useState(true);
    const [saving, setSaving] = useState(false);

    const addRow = () => setIngredients((p) => [...p, { item_id: '', item_name: '', qty: '', uom: 'g' }]);
    const removeRow = (idx) => setIngredients((p) => p.filter((_, i) => i !== idx));
    const updateRow = (idx, patch) =>
        setIngredients((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

    const canSave = ingredients.length > 0 && ingredients.every((r) => {
        const q = Number(r.qty);
        return r.item_id && Number.isFinite(q) && q > 0 && r.uom;
    });

    async function submit() {
        if (!canSave) {
            toast.error('Fill every ingredient row with a raw material, qty and UOM.');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                menu_item_id: menuItem.id,
                ingredients: ingredients.map((r) => ({
                    item_id: r.item_id,
                    item_name: r.item_name || r.item_id,
                    qty: Number(r.qty),
                    uom: r.uom,
                })),
                notes: notes.trim() || null,
                retro_apply: retroApply,
            };
            const r = await api.createRecipe(payload);
            const retro = r.retro || {};
            const retroMsg = retro.ledger_entries_written
                ? ` Retro-deducted ${retro.sales_deducted.toFixed(0)} past sales into ${retro.ledger_entries_written} ledger rows.`
                : '';
            toast.success(`Recipe saved for "${menuItem.name}".${retroMsg}`, { duration: 6000 });
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save recipe.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recipe-builder-dialog">
                <DialogHeader>
                    <DialogTitle>Recipe for “{menuItem.name}”</DialogTitle>
                    <DialogDescription>
                        Add each raw material this dish uses per single serving. Once
                        saved, every future Petpooja sale of this item will auto-deduct
                        the recipe from your stock ledger.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Ingredients per serving
                    </div>
                    {ingredients.map((row, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr,90px,110px,auto] gap-2 items-end">
                            <div>
                                <Label className="text-[11px]">Raw material</Label>
                                <Select
                                    value={row.item_id}
                                    onValueChange={(v) => {
                                        const mat = rawMaterials.find((m) => m.id === v);
                                        updateRow(idx, {
                                            item_id: v,
                                            item_name: mat?.display_name || v,
                                            uom: mat?.stock_uom || row.uom,
                                        });
                                    }}
                                >
                                    <SelectTrigger data-testid={`recipe-ingredient-picker-${idx}`}>
                                        <SelectValue placeholder="Pick a raw material" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {rawMaterials.length === 0 && (
                                            <div className="px-3 py-2 text-xs text-muted-foreground">
                                                No raw materials yet. Scan a vendor bill first.
                                            </div>
                                        )}
                                        {rawMaterials.map((m) => (
                                            <SelectItem key={m.id} value={m.id}>
                                                {m.display_name}
                                                <span className="ml-2 text-[10px] text-muted-foreground uppercase">
                                                    {m.stock_uom}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[11px]">Qty</Label>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="0.001"
                                    value={row.qty}
                                    onChange={(e) => updateRow(idx, { qty: e.target.value })}
                                    className="font-mono tabular-nums"
                                    data-testid={`recipe-qty-${idx}`}
                                />
                            </div>
                            <div>
                                <Label className="text-[11px]">UOM</Label>
                                <Select value={row.uom} onValueChange={(v) => updateRow(idx, { uom: v })}>
                                    <SelectTrigger data-testid={`recipe-uom-${idx}`}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {uomList.map((u) => (
                                            <SelectItem key={u.key} value={u.key}>
                                                {u.key}
                                                <span className="ml-2 text-[10px] text-muted-foreground">
                                                    {u.class}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-destructive"
                                onClick={() => removeRow(idx)}
                                disabled={ingredients.length === 1}
                                title="Remove"
                                data-testid={`recipe-remove-${idx}`}
                            >
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    ))}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={addRow}
                        className="gap-2"
                        data-testid="recipe-add-ingredient"
                    >
                        <Plus size={14} /> Add ingredient
                    </Button>

                    <div className="pt-3 border-t border-border/60">
                        <Label className="text-[11px]">Notes (optional)</Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. seasonal recipe · double portion"
                            className="mt-1"
                            data-testid="recipe-notes"
                        />
                    </div>

                    <label className="flex items-start gap-2 rounded-md border border-border/70 bg-muted/30 p-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={retroApply}
                            onChange={(e) => setRetroApply(e.target.checked)}
                            className="mt-0.5"
                            data-testid="recipe-retro-apply"
                        />
                        <div className="space-y-0.5">
                            <div className="text-sm font-medium">Apply to past sales</div>
                            <div className="text-[11px] text-muted-foreground">
                                Retro-deduct stock for every previous Petpooja sale of this
                                menu item. Recommended so your stock levels reflect reality
                                immediately.
                            </div>
                        </div>
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving || !canSave}
                        data-testid="recipe-save"
                    >
                        {saving ? 'Saving…' : (existing ? 'Save changes' : 'Save recipe')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─────────────────────────────────────────────────────────────
 * SellingPriceCell — inline-editable ₹ input per menu item.
 * Blur or Enter → PATCH /api/menu-items/{id}. Clear (empty) → null.
 * ───────────────────────────────────────────────────────────── */
function SellingPriceCell({ menuItem, onSaved }) {
    const [val, setVal] = useState(
        menuItem.selling_price != null ? String(menuItem.selling_price) : '',
    );
    const [saving, setSaving] = useState(false);

    async function commit() {
        const trimmed = val.trim();
        // Detect no change
        const cur = menuItem.selling_price != null ? String(menuItem.selling_price) : '';
        if (trimmed === cur) return;
        const num = trimmed === '' ? null : Number(trimmed);
        if (num !== null && (!Number.isFinite(num) || num < 0)) {
            toast.error('Selling price must be a positive number.');
            setVal(cur);
            return;
        }
        setSaving(true);
        try {
            await api.updateMenuItem(menuItem.id, { selling_price: num });
            onSaved(num);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save.');
            setVal(cur);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') { e.currentTarget.blur(); }
                if (e.key === 'Escape') {
                    setVal(menuItem.selling_price != null ? String(menuItem.selling_price) : '');
                    e.currentTarget.blur();
                }
            }}
            disabled={saving}
            placeholder="—"
            className="h-8 w-20 text-right font-mono tabular-nums ml-auto"
            data-testid={`menu-price-${menuItem.id}`}
        />
    );
}


function MarginCell({ margin, defaultThreshold }) {
    if (!margin || margin.food_cost_pct == null) {
        return <span className="text-xs text-muted-foreground">—</span>;
    }
    const pct = margin.food_cost_pct;
    const threshold = margin.threshold_pct || defaultThreshold;
    const breached = margin.breached;
    const cls = breached
        ? 'text-red-700 dark:text-red-400 font-semibold'
        : pct >= (threshold * 0.85)
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-green-700 dark:text-green-400';
    return (
        <span
            className={`inline-flex items-center gap-1 font-mono tabular-nums ${cls}`}
            title={`Threshold ${threshold}% · Margin ₹${margin.margin ?? '—'}`}
            data-testid="margin-cell"
        >
            {breached ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {pct.toFixed(1)}%
        </span>
    );
}


/* ─────────────────────────────────────────────────────────────
 * PrepRecipes — Phase 7c Feature #4.
 * Manages prep items (intermediate inventory) + their recipes +
 * production batches. Prep batches balance the ledger (raw
 * ingredients go OUT, prep item goes IN).
 * ───────────────────────────────────────────────────────────── */
function PrepRecipes() {
    const [prepItems, setPrepItems] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [uomList, setUomList] = useState([]);
    const [creatingItem, setCreatingItem] = useState(false);
    const [buildingFor, setBuildingFor] = useState(null);   // prep_item being recipe-built/edited
    const [logging, setLogging] = useState(null);           // prep recipe to log a batch for
    const [deletingItem, setDeletingItem] = useState(null); // prep_item currently mid-delete

    const load = useCallback(async () => {
        setPrepItems(null);
        try {
            const [p, r, mats, uom] = await Promise.all([
                api.listPrepItems(),
                api.listRecipes(),
                api.stockRawMaterials(),
                api.stockUomMaster(),
            ]);
            setPrepItems(p.items || []);
            setRecipes((r.items || []).filter((x) => x.recipe_type === 'prep'));
            setRawMaterials(mats.items || []);
            setUomList(uom.units || []);
        } catch (e) {
            setPrepItems([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const recipeByYield = useMemo(() => {
        const m = {};
        for (const r of recipes) if (r.yields_item_id) m[r.yields_item_id] = r;
        return m;
    }, [recipes]);

    /**
     * Delete a prep item, cascading references only after explicit confirm.
     * Backend returns 409 with a `blockers` payload if the item is referenced
     * by any recipe or has ledger history — we surface a plain-English
     * confirmation before retrying with `force=true`.
     */
    async function handleDelete(prepItem) {
        setDeletingItem(prepItem.id);
        try {
            await api.deletePrepItem(prepItem.id);
            toast.success(`Deleted "${prepItem.display_name}"`);
            await load();
            return;
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const status = e?.response?.status;
            if (status !== 409 || typeof detail !== 'object' || !detail?.blockers) {
                toast.error(typeof detail === 'string' ? detail : 'Delete failed.');
                setDeletingItem(null);
                return;
            }
            const b = detail.blockers;
            const parts = [];
            if (b.yields_from_recipes?.length) {
                parts.push(`• ${b.yields_from_recipes.length} prep recipe(s) that yield this item`);
            }
            if (b.used_as_ingredient_in?.length) {
                parts.push(`• ${b.used_as_ingredient_in.length} recipe(s) using it as ingredient`);
            }
            if (b.ledger_entries) {
                parts.push(`• ${b.ledger_entries} stock ledger entrie(s)`);
            }
            // eslint-disable-next-line no-alert
            const ok = window.confirm(
                `"${prepItem.display_name}" is referenced by:\n\n${parts.join('\n')}\n\n`
                + 'Force delete will also remove the linked prep recipe(s) and clear the '
                + 'ledger history. This cannot be undone. Proceed?',
            );
            if (!ok) { setDeletingItem(null); return; }
            try {
                const res = await api.deletePrepItem(prepItem.id, { force: true });
                const c = res.cascaded || {};
                toast.success(
                    `Deleted "${prepItem.display_name}" · ${c.recipes_deleted || 0} recipe(s), `
                    + `${c.ledger_entries_deleted || 0} ledger entries removed`,
                );
                await load();
            } catch (e2) {
                toast.error(e2?.response?.data?.detail?.error || e2?.response?.data?.detail || 'Force delete failed.');
            }
        } finally {
            setDeletingItem(null);
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        <Beaker size={18} className="text-primary" /> Prep recipes
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-xl">
                        Track intermediate bases you prep in the morning (e.g. Chocolate
                        Base). Menu recipes deduct from the prepped base instead of raw
                        ingredients — keeping your ledger accurate.
                    </p>
                </div>
                <Button size="sm" className="gap-1" onClick={() => setCreatingItem(true)} data-testid="prep-new-item">
                    <Plus size={13} /> New prep item
                </Button>
            </div>

            <Card className="p-0 overflow-hidden">
                {prepItems === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : prepItems.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                            <Beaker size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No prep items yet</div>
                        <div className="text-sm text-muted-foreground max-w-md mx-auto">
                            Create your first intermediate base (e.g. &ldquo;Chocolate Base&rdquo;)
                            so recipes can consume it directly.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Prep item</th>
                                    <th className="text-left px-4 py-2 font-medium">Stock UOM</th>
                                    <th className="text-left px-4 py-2 font-medium">Recipe</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prepItems.map((p) => {
                                    const recipe = recipeByYield[p.id];
                                    return (
                                        <tr key={p.id} className="border-t border-border hover:bg-muted/30">
                                            <td className="px-4 py-2 font-medium">{p.display_name}</td>
                                            <td className="px-4 py-2 text-xs uppercase text-muted-foreground">{p.stock_uom}</td>
                                            <td className="px-4 py-2">
                                                {recipe ? (
                                                    <span className="text-xs text-muted-foreground">
                                                        Yields {recipe.yields_qty} {recipe.yields_uom} · {recipe.ingredients.length} ing
                                                    </span>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] gap-1 border-amber-400/60 text-amber-700 dark:text-amber-300">
                                                        <AlertCircle size={10} /> No recipe
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {recipe && (
                                                        <Button size="sm" variant="default" className="gap-1 h-8"
                                                            onClick={() => setLogging(recipe)} data-testid={`prep-log-${p.id}`}>
                                                            <Play size={12} /> Log batch
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant={recipe ? 'ghost' : 'default'} className="gap-1"
                                                        onClick={() => setBuildingFor(p)} data-testid={`prep-build-${p.id}`}>
                                                        {recipe ? <><Pencil size={12} /> Recipe</> : <><Sparkles size={12} /> Build</>}
                                                    </Button>
                                                    <Button
                                                        size="sm" variant="ghost"
                                                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                                        onClick={() => handleDelete(p)}
                                                        disabled={deletingItem === p.id}
                                                        title="Delete prep item"
                                                        data-testid={`prep-delete-${p.id}`}
                                                    >
                                                        <Trash2 size={13} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {creatingItem && (
                <NewPrepItemDialog
                    open={creatingItem}
                    onOpenChange={setCreatingItem}
                    uomList={uomList}
                    onSaved={async () => { setCreatingItem(false); await load(); }}
                />
            )}
            {buildingFor && (
                <PrepRecipeDialog
                    open={!!buildingFor}
                    onOpenChange={(v) => { if (!v) setBuildingFor(null); }}
                    prepItem={buildingFor}
                    existing={recipeByYield[buildingFor.id]}
                    rawMaterials={rawMaterials}
                    uomList={uomList}
                    onSaved={async () => { setBuildingFor(null); await load(); }}
                />
            )}
            {logging && (
                <LogPrepBatchDialog
                    open={!!logging}
                    onOpenChange={(v) => { if (!v) setLogging(null); }}
                    recipe={logging}
                    onSaved={async () => { setLogging(null); await load(); }}
                />
            )}
        </div>
    );
}


function NewPrepItemDialog({ open, onOpenChange, uomList, onSaved }) {
    const [name, setName] = useState('');
    const [uom, setUom] = useState('ml');
    const [saving, setSaving] = useState(false);
    async function submit() {
        const nm = name.trim();
        if (!nm) { toast.error('Name is required.'); return; }
        setSaving(true);
        try {
            await api.createPrepItem({ name: nm, stock_uom: uom });
            toast.success(`Prep item "${nm}" created.`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to create.');
        } finally { setSaving(false); }
    }
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent data-testid="new-prep-item-dialog">
                <DialogHeader>
                    <DialogTitle>New prep item</DialogTitle>
                    <DialogDescription>
                        Create an intermediate inventory item (e.g. &ldquo;Chocolate Base&rdquo;).
                        Menu recipes can then consume it directly.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Chocolate Base" className="mt-1"
                            data-testid="new-prep-item-name" autoFocus />
                    </div>
                    <div>
                        <Label className="text-xs">Stock UOM</Label>
                        <Select value={uom} onValueChange={setUom}>
                            <SelectTrigger className="mt-1" data-testid="new-prep-item-uom">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                {uomList.map((u) => (
                                    <SelectItem key={u.key} value={u.key}>
                                        {u.key} <span className="text-muted-foreground text-xs ml-2">({u.class})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PrepRecipeDialog({ open, onOpenChange, prepItem, existing, rawMaterials, uomList, onSaved }) {
    // Prep recipes look like menu recipes + yield fields.
    const [ingredients, setIngredients] = useState(() => {
        if (existing?.ingredients?.length) return existing.ingredients.map((i) => ({ ...i }));
        return [{ item_id: '', item_name: '', qty: '', uom: 'g' }];
    });
    const [yieldsQty, setYieldsQty] = useState(existing?.yields_qty != null ? String(existing.yields_qty) : '');
    const [yieldsUom, setYieldsUom] = useState(existing?.yields_uom || prepItem.stock_uom || 'ml');
    const [notes, setNotes] = useState(existing?.notes || '');
    const [saving, setSaving] = useState(false);

    const addRow = () => setIngredients((p) => [...p, { item_id: '', item_name: '', qty: '', uom: 'g' }]);
    const removeRow = (idx) => setIngredients((p) => p.filter((_, i) => i !== idx));
    const updateRow = (idx, patch) =>
        setIngredients((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

    async function submit() {
        const yq = Number(yieldsQty);
        if (!Number.isFinite(yq) || yq <= 0) {
            toast.error('Enter a positive yield quantity.');
            return;
        }
        for (const r of ingredients) {
            if (!r.item_id || !Number.isFinite(Number(r.qty)) || Number(r.qty) <= 0 || !r.uom) {
                toast.error('Fill every ingredient row.');
                return;
            }
        }
        setSaving(true);
        try {
            await api.createRecipe({
                menu_item_id: '',
                recipe_type: 'prep',
                yields_item_id: prepItem.id,
                yields_qty: yq,
                yields_uom: yieldsUom,
                ingredients: ingredients.map((r) => ({
                    item_id: r.item_id, item_name: r.item_name || r.item_id,
                    qty: Number(r.qty), uom: r.uom,
                })),
                notes: notes.trim() || null,
                retro_apply: false,   // prep recipes don't apply retro to Petpooja
            });
            toast.success(`Prep recipe saved. Yields ${yq} ${yieldsUom} per batch.`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prep-recipe-dialog">
                <DialogHeader>
                    <DialogTitle>Prep recipe · {prepItem.display_name}</DialogTitle>
                    <DialogDescription>
                        Ingredients to make one batch, and how much it yields.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-[1fr,100px] gap-2 items-end p-3 rounded-md border border-primary/40 bg-primary/5">
                        <div>
                            <Label className="text-[11px] text-muted-foreground">One batch yields</Label>
                            <Input type="number" inputMode="decimal" min="0" step="0.01" value={yieldsQty}
                                onChange={(e) => setYieldsQty(e.target.value)}
                                placeholder="e.g. 5000"
                                className="mt-1 font-mono tabular-nums"
                                data-testid="prep-yields-qty" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-muted-foreground">of</Label>
                            <Select value={yieldsUom} onValueChange={setYieldsUom}>
                                <SelectTrigger className="mt-1" data-testid="prep-yields-uom">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {uomList.map((u) => <SelectItem key={u.key} value={u.key}>{u.key}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground pt-1">Ingredients per batch</div>
                    {ingredients.map((row, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr,90px,110px,auto] gap-2 items-end">
                            <div>
                                <Label className="text-[11px]">Raw material</Label>
                                <Select value={row.item_id} onValueChange={(v) => {
                                    const mat = rawMaterials.find((m) => m.id === v);
                                    updateRow(idx, { item_id: v, item_name: mat?.display_name || v, uom: mat?.stock_uom || row.uom });
                                }}>
                                    <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {rawMaterials.map((m) => (
                                            <SelectItem key={m.id} value={m.id}>{m.display_name}
                                                <span className="ml-2 text-[10px] text-muted-foreground uppercase">{m.stock_uom}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[11px]">Qty</Label>
                                <Input type="number" inputMode="decimal" min="0" step="0.001" value={row.qty}
                                    onChange={(e) => updateRow(idx, { qty: e.target.value })}
                                    className="font-mono tabular-nums" />
                            </div>
                            <div>
                                <Label className="text-[11px]">UOM</Label>
                                <Select value={row.uom} onValueChange={(v) => updateRow(idx, { uom: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {uomList.map((u) => <SelectItem key={u.key} value={u.key}>{u.key}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive"
                                onClick={() => removeRow(idx)} disabled={ingredients.length === 1}>
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addRow} className="gap-2">
                        <Plus size={14} /> Add ingredient
                    </Button>
                    <div className="pt-3 border-t border-border/60">
                        <Label className="text-[11px]">Notes (optional)</Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="prep-recipe-save">
                        {saving ? 'Saving…' : 'Save recipe'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function LogPrepBatchDialog({ open, onOpenChange, recipe, onSaved }) {
    const [batches, setBatches] = useState('1');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const b = Number(batches);
    const canSave = Number.isFinite(b) && b > 0;
    const totalYield = canSave ? (b * Number(recipe.yields_qty)).toFixed(2) : '—';

    async function submit() {
        if (!canSave) return;
        setSaving(true);
        try {
            const r = await api.logPrepBatch({
                recipe_id: recipe.id,
                batches: b,
                notes: notes.trim() || null,
            });
            toast.success(
                `Logged ${b} batch(es). ${r.batch.yields_qty_total} ${recipe.yields_uom} of ${recipe.yields_item_name} added to stock.`,
                { duration: 6000 },
            );
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to log batch.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent data-testid="log-prep-batch-dialog">
                <DialogHeader>
                    <DialogTitle>Log prep batch · {recipe.yields_item_name}</DialogTitle>
                    <DialogDescription>
                        Records production. Raw ingredients go OUT of stock and the
                        prep item goes IN — automatically.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Batches produced</Label>
                        <Input type="number" inputMode="decimal" min="0" step="0.5" value={batches}
                            onChange={(e) => setBatches(e.target.value)}
                            className="mt-1 font-mono tabular-nums" autoFocus
                            data-testid="log-prep-batches" />
                        <p className="text-[11px] text-muted-foreground mt-1">
                            One batch yields {recipe.yields_qty} {recipe.yields_uom}.
                            Total this run: <span className="font-mono">{totalYield} {recipe.yields_uom}</span>
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs">Notes (optional)</Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. morning prep · double batch" className="mt-1" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !canSave} data-testid="log-prep-batch-save">
                        {saving ? 'Logging…' : 'Log batch'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



/* ═══════════════════════════════════════════════════════════════
 * Phase 7e — Production Recipes
 * Recipes where recipe_type === 'production' — they yield packed
 * finished goods with barcodes. Editable SKU / shelf-life / veg flag.
 * ═════════════════════════════════════════════════════════════ */
function ProductionRecipes() {
    const [recipes, setRecipes] = useState(null);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [uomList, setUomList] = useState([]);
    const [editing, setEditing] = useState(null);   // {} for new; recipe object for edit

    const load = useCallback(async () => {
        setRecipes(null);
        try {
            const [r, mats, uom] = await Promise.all([
                api.listRecipes(),
                api.stockRawMaterials(),
                api.stockUomMaster(),
            ]);
            setRecipes((r.items || []).filter((x) => x.recipe_type === 'production'));
            setRawMaterials(mats.items || []);
            setUomList(uom.units || []);
        } catch { setRecipes([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        <Factory size={18} className="text-primary" /> Production recipes
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-xl">
                        Recipes that produce packed, barcoded finished goods (e.g. Vanilla
                        Ice-cream 500 ml tubs). Each production run deducts raw materials
                        and creates traceable lots with FSSAI labels.
                    </p>
                </div>
                <Button size="sm" className="gap-1" onClick={() => setEditing({})} data-testid="production-recipe-new">
                    <Plus size={13} /> New production recipe
                </Button>
            </div>

            <Card className="p-0 overflow-hidden">
                {recipes === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : recipes.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                            <Factory size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No production recipes yet</div>
                        <div className="text-sm text-muted-foreground max-w-md mx-auto">
                            Create your first production recipe (SKU + yield + shelf-life)
                            to start manufacturing packed goods.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">SKU</th>
                                    <th className="text-left px-4 py-2 font-medium">Recipe name</th>
                                    <th className="text-right px-4 py-2 font-medium">Yield / batch</th>
                                    <th className="text-right px-4 py-2 font-medium">Yield adj</th>
                                    <th className="text-right px-4 py-2 font-medium">Shelf life</th>
                                    <th className="text-left px-4 py-2 font-medium">Veg</th>
                                    <th className="text-right px-4 py-2 font-medium">Ingredients</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recipes.map((r, i) => (
                                    <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`production-recipe-row-${i}`}>
                                        <td className="px-4 py-2 font-mono text-xs uppercase">{r.sku_code || '—'}</td>
                                        <td className="px-4 py-2 font-medium">{r.menu_item_name || r.notes || r.id.slice(0, 8)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">
                                            {r.yields_qty} <span className="text-[10px] text-muted-foreground">{r.yields_uom}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono text-xs text-muted-foreground">
                                            {r.yield_adjustment_pct ? `${r.yield_adjustment_pct > 0 ? '+' : ''}${r.yield_adjustment_pct}%` : '—'}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums text-xs">{r.shelf_life_days || 90}d</td>
                                        <td className="px-4 py-2">
                                            {r.is_veg
                                                ? <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400 text-[10px]">VEG</Badge>
                                                : <Badge variant="outline" className="border-red-500 text-red-700 dark:text-red-400 text-[10px]">NON-VEG</Badge>}
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{r.ingredients?.length || 0}</td>
                                        <td className="px-4 py-2 text-right">
                                            <Button size="sm" variant="ghost" className="gap-1 h-7" onClick={() => setEditing(r)} data-testid={`production-recipe-edit-${i}`}>
                                                <Pencil size={12} /> Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {editing !== null && (
                <ProductionRecipeDialog
                    open={editing !== null}
                    onOpenChange={(v) => { if (!v) setEditing(null); }}
                    existing={editing.id ? editing : null}
                    rawMaterials={rawMaterials}
                    uomList={uomList}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </div>
    );
}


function ProductionRecipeDialog({ open, onOpenChange, existing, rawMaterials, uomList, onSaved }) {
    const [name, setName] = useState(existing?.menu_item_name || existing?.notes || '');
    const [skuCode, setSkuCode] = useState(existing?.sku_code || '');
    const [yieldsQty, setYieldsQty] = useState(existing?.yields_qty != null ? String(existing.yields_qty) : '');
    const [yieldsUom, setYieldsUom] = useState(existing?.yields_uom || 'ml');
    const [yieldAdj, setYieldAdj] = useState(existing?.yield_adjustment_pct != null ? String(existing.yield_adjustment_pct) : '0');
    const [shelfLife, setShelfLife] = useState(existing?.shelf_life_days ? String(existing.shelf_life_days) : '90');
    const [isVeg, setIsVeg] = useState(existing?.is_veg !== false);
    const [notes, setNotes] = useState(existing?.notes || '');
    const [ingredients, setIngredients] = useState(() => {
        if (existing?.ingredients?.length) return existing.ingredients.map((i) => ({ ...i }));
        return [{ item_id: '', item_name: '', qty: '', uom: 'g' }];
    });
    const [saving, setSaving] = useState(false);

    const addRow = () => setIngredients((p) => [...p, { item_id: '', item_name: '', qty: '', uom: 'g' }]);
    const removeRow = (i) => setIngredients((p) => p.filter((_, idx) => idx !== i));
    const updateRow = (idx, patch) => setIngredients((p) => p.map((r, i) => i === idx ? { ...r, ...patch } : r));

    async function submit() {
        const sku = (skuCode || '').trim().toUpperCase();
        if (!sku || sku.length < 2 || sku.length > 8) { toast.error('SKU code must be 2-8 chars.'); return; }
        const yq = Number(yieldsQty);
        if (!Number.isFinite(yq) || yq <= 0) { toast.error('Enter a positive yield quantity.'); return; }
        for (const r of ingredients) {
            if (!r.item_id || !Number.isFinite(Number(r.qty)) || Number(r.qty) <= 0 || !r.uom) {
                toast.error('Fill every ingredient row.'); return;
            }
        }
        setSaving(true);
        try {
            if (existing) {
                await api.updateRecipe(existing.id, {
                    ingredients: ingredients.map((r) => ({
                        item_id: r.item_id, item_name: r.item_name || r.item_id,
                        qty: Number(r.qty), uom: r.uom,
                    })),
                    notes: name.trim() || notes.trim() || null,
                    sku_code: sku,
                    yields_qty: yq,
                    yields_uom: yieldsUom,
                    yield_adjustment_pct: Number(yieldAdj) || 0,
                    shelf_life_days: Number(shelfLife) || 90,
                    is_veg: !!isVeg,
                });
            } else {
                await api.createRecipe({
                    menu_item_id: '',
                    recipe_type: 'production',
                    yields_qty: yq,
                    yields_uom: yieldsUom,
                    sku_code: sku,
                    yield_adjustment_pct: Number(yieldAdj) || 0,
                    shelf_life_days: Number(shelfLife) || 90,
                    is_veg: !!isVeg,
                    ingredients: ingredients.map((r) => ({
                        item_id: r.item_id, item_name: r.item_name || r.item_id,
                        qty: Number(r.qty), uom: r.uom,
                    })),
                    notes: name.trim() || null,
                    retro_apply: false,
                });
            }
            toast.success(`Production recipe ${existing ? 'updated' : 'created'}: ${sku}`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="production-recipe-dialog">
                <DialogHeader>
                    <DialogTitle>{existing ? 'Edit production recipe' : 'New production recipe'}</DialogTitle>
                    <DialogDescription>
                        Define ingredients, yield per batch, SKU, and shelf life. Used by the Production tab to run batches.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs mb-1.5 block">Recipe name / label</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Vanilla Ice-cream" data-testid="production-recipe-name" />
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">SKU code (2-8 chars)</Label>
                            <Input value={skuCode} onChange={(e) => setSkuCode(e.target.value.toUpperCase())}
                                placeholder="e.g. VNL" className="uppercase font-mono" data-testid="production-recipe-sku" />
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                        <div className="col-span-2">
                            <Label className="text-xs mb-1.5 block">Yield / batch</Label>
                            <div className="grid grid-cols-[1fr,90px] gap-1">
                                <Input type="number" step="0.01" min="0" value={yieldsQty}
                                    onChange={(e) => setYieldsQty(e.target.value)}
                                    placeholder="e.g. 5000" className="font-mono"
                                    data-testid="production-recipe-yield-qty" />
                                <Select value={yieldsUom} onValueChange={setYieldsUom}>
                                    <SelectTrigger data-testid="production-recipe-yield-uom">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                        {uomList.map((u) => <SelectItem key={u.key} value={u.key}>{u.key}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">Yield adj %</Label>
                            <Input type="number" step="1" value={yieldAdj}
                                onChange={(e) => setYieldAdj(e.target.value)}
                                placeholder="e.g. -5 or +50" className="font-mono"
                                data-testid="production-recipe-yield-adj" />
                        </div>
                        <div>
                            <Label className="text-xs mb-1.5 block">Shelf life (days)</Label>
                            <Input type="number" min="1" value={shelfLife}
                                onChange={(e) => setShelfLife(e.target.value)}
                                placeholder="90" className="font-mono"
                                data-testid="production-recipe-shelf-life" />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Label className="text-xs">Veg mark:</Label>
                        <div className="flex gap-1">
                            <Button size="sm" variant={isVeg ? 'default' : 'outline'} onClick={() => setIsVeg(true)}
                                className="h-7" data-testid="production-recipe-veg">VEG</Button>
                            <Button size="sm" variant={!isVeg ? 'default' : 'outline'} onClick={() => setIsVeg(false)}
                                className="h-7" data-testid="production-recipe-nonveg">NON-VEG</Button>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs">Ingredients (per one batch)</Label>
                            <Button size="sm" variant="outline" onClick={addRow} className="gap-1 h-7" data-testid="production-recipe-add-ing">
                                <Plus size={11} /> Add
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {ingredients.map((r, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <div className="col-span-6">
                                        <Select
                                            value={r.item_id || ''}
                                            onValueChange={(v) => {
                                                const mat = rawMaterials.find((m) => m.id === v);
                                                updateRow(i, { item_id: v, item_name: mat?.display_name || v, uom: mat?.stock_uom || r.uom });
                                            }}
                                        >
                                            <SelectTrigger className="h-8 text-sm" data-testid={`production-recipe-ing-${i}-item`}>
                                                <SelectValue placeholder="Raw material…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {rawMaterials.map((m) => (
                                                    <SelectItem key={m.id} value={m.id}>
                                                        {m.display_name} <span className="text-muted-foreground text-xs ml-1">({m.stock_uom})</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-3">
                                        <Input type="number" step="0.01" min="0" value={r.qty}
                                            onChange={(e) => updateRow(i, { qty: e.target.value })}
                                            placeholder="Qty" className="h-8 font-mono"
                                            data-testid={`production-recipe-ing-${i}-qty`} />
                                    </div>
                                    <div className="col-span-2">
                                        <Select value={r.uom} onValueChange={(v) => updateRow(i, { uom: v })}>
                                            <SelectTrigger className="h-8 text-sm" data-testid={`production-recipe-ing-${i}-uom`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-64">
                                                {uomList.map((u) => <SelectItem key={u.key} value={u.key}>{u.key}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        {ingredients.length > 1 && (
                                            <Button size="sm" variant="ghost" onClick={() => removeRow(i)} className="h-8 w-8 p-0" data-testid={`production-recipe-ing-${i}-remove`}>
                                                <Trash2 size={13} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="production-recipe-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="production-recipe-save">
                        {saving ? 'Saving…' : (existing ? 'Save changes' : 'Create recipe')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
